import {
	MinusOutlined,
	PlusOutlined,
	QuestionCircleOutlined,
} from "@ant-design/icons";
import { Form, Radio, Switch, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { CategoryCancelConditions } from "./CategoryCancelConditions";
import { CategoryEventMessage } from "./CategoryEventMessage";
import { Fee } from "./Fee";

interface EventSettingsDetailProps {
	open: boolean;
	defaultExpanded?: boolean;
	isProgram?: boolean;
	categoryId?: number;
	primaryColor?: string;
	isPaymentAvailable?: boolean;
	showUniqueMessage?: boolean;
	showCancellationSettings?: boolean;
	showProgramSettings?: boolean;
}

export const EventSettingsDetail = ({
	open,
	defaultExpanded = false,
	isProgram = false,
	categoryId,
	primaryColor = "#99CA29",
	isPaymentAvailable,
	showUniqueMessage = true,
	showCancellationSettings = true,
	showProgramSettings = true,
}: EventSettingsDetailProps) => {
	const [isExpanded, setIsExpanded] = useState(false);

	useEffect(() => {
		if (!open) {
			setIsExpanded(false);
		}
	}, [open]);

	useEffect(() => {
		if (open && defaultExpanded) {
			setIsExpanded(true);
		}
	}, [open, defaultExpanded]);

	return (
		<div className="mb-4">
			<button
				type="button"
				onClick={() => setIsExpanded(!isExpanded)}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						setIsExpanded(!isExpanded);
					}
				}}
				className="w-full h-9 px-6 bg-gray-200 border-none flex items-center justify-between cursor-pointer rounded outline-none focus:ring-2 focus:ring-gray-400 focus:ring-inset"
			>
				<span></span>
				<span className="select-none font-medium">イベント設定詳細</span>
				<div className="border border-gray-500 rounded w-4 h-4 flex items-center justify-center text-[10px]">
					{isExpanded ? <MinusOutlined /> : <PlusOutlined />}
				</div>
			</button>

			<div className={`mt-4 ${isExpanded ? "block" : "hidden"}`}>
				{!isProgram && showProgramSettings && (
					<div className="flex items-center gap-4 mb-4">
						<div className="flex items-center gap-2">
							<span>イベントプログラム設定</span>
							<Tooltip
								overlayInnerStyle={{ width: 300 }}
								title="イベントの詳細プログラム毎に予約を分けたい場合は「あり」を選択ください。"
							>
								<QuestionCircleOutlined style={{ color: primaryColor }} />
							</Tooltip>
						</div>
						<Form.Item noStyle name="isProgram">
							<Switch disabled={!!categoryId} />
						</Form.Item>
					</div>
				)}

				<Form.Item
					noStyle
					shouldUpdate={(prevValues, currentValues) =>
						prevValues.isProgram !== currentValues.isProgram
					}
				>
					{({ getFieldValue }) => {
						const isProgramValue = getFieldValue("isProgram");
						if (isProgramValue) return null;

						return (
							<div className="border border-dashed border-gray-300 rounded-md p-4">
								<div className="flex items-center gap-4 h-8 mb-2">
									<div className="flex items-center gap-2">
										<span>他イベントとの同時予約</span>
										<Tooltip
											overlayInnerStyle={{ width: 450 }}
											title={
												<div>
													<div>■OKの場合</div>
													<div>・予約者は他イベントにも予約できます。</div>
													<div>■NGの場合</div>
													<div>
														・予約者はイベント終了まで他イベントに予約できなくなります。
													</div>
													<div>
														・他イベント予約者はこのイベントには予約できなくなります。
													</div>
												</div>
											}
										>
											<QuestionCircleOutlined style={{ color: primaryColor }} />
										</Tooltip>
									</div>
									<Form.Item noStyle name="isMultiEvent">
										<Radio.Group>
											<Radio value={true}>OK</Radio>
											<Radio value={false}>NG</Radio>
										</Radio.Group>
									</Form.Item>
								</div>

								<Form.Item
									noStyle
									dependencies={["isSettingTime", "isMultiEvent"]}
								>
									{({ getFieldValue }) => {
										const isSettingTime = getFieldValue("isSettingTime");
										const isMultiEvent = getFieldValue("isMultiEvent");
										if (isSettingTime || !isMultiEvent) return null;

										return (
											<div className="flex items-center gap-4 h-8 mb-2">
												<div className="flex items-center gap-2">
													<span>他イベントとの時間重複予約</span>
													<Tooltip
														overlayInnerStyle={{ width: 390 }}
														title={
															<div>
																<div>■OKの場合</div>
																<div>
																	・時間が重複していても他イベントと重複予約できます。
																</div>
																<div>■NGの場合</div>
																<div>
																	・時間が重複している場合は予約できなくなります。
																</div>
															</div>
														}
													>
														<QuestionCircleOutlined
															style={{ color: primaryColor }}
														/>
													</Tooltip>
												</div>
												<Form.Item noStyle name="notRegisterEventSameTime">
													<Radio.Group>
														<Radio value={false}>OK</Radio>
														<Radio value={true}>NG</Radio>
													</Radio.Group>
												</Form.Item>
											</div>
										);
									}}
								</Form.Item>

								{!isProgram && (
									<Fee
										isPaymentAvailable={isPaymentAvailable}
										primaryColor={primaryColor}
									/>
								)}

								{showUniqueMessage && (
									<div className="mt-2">
										<CategoryEventMessage />
									</div>
								)}

								{!isProgram && showCancellationSettings && (
									<div className="mt-2">
										<CategoryCancelConditions />
									</div>
								)}
							</div>
						);
					}}
				</Form.Item>
			</div>
		</div>
	);
};
